import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCr, GATE_LABELS } from "@/lib/utils-ros";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { FunnelBoard } from "@/components/revenueos/FunnelBoard";
import { ParkOpportunityModal } from "@/components/revenueos/ParkOpportunityModal";

function loadErrorMessage(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return { status, message: detail };
  if (status === 403) return { status, message: "You don't have permission to view the funnel." };
  return { status, message: "Could not load opportunities." };
}

/**
 * Standalone Funnel page — same vertical funnel board the Opportunities page's
 * "Funnel" view toggle renders, just with its own hero header and full-width layout.
 */
export default function Funnel() {
  const { canEditOpportunities } = useAuth();
  const canMoveDeals = canEditOpportunities();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moving, setMoving] = useState(null);
  const [parkTarget, setParkTarget] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/opportunities");
      setOpps(data);
    } catch (err) {
      setError(loadErrorMessage(err));
      setOpps([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function move(id, gate) {
    setMoving(id);
    try {
      await api.patch(`/opportunities/${id}`, { current_gate: gate });
      toast.success(`Moved to ${GATE_LABELS[gate]}`);
      await load();
    } catch {
      toast.error("Could not move deal — check permissions and try again");
    } finally { setMoving(null); }
  }

  async function park(id, status) {
    setMoving(id);
    try {
      await api.patch(`/opportunities/${id}`, { status });
      toast.success(status === "parked" ? "Parked" : "Reactivated");
      await load();
    } catch {
      toast.error("Update failed");
    } finally { setMoving(null); }
  }

  const active = opps.filter(o => o.status === "active");
  const totalValue = active.reduce((s, o) => s + (o.value_cr || 0), 0);
  const weighted = active.reduce((s, o) => s + (o.value_cr || 0) * (o.probability || 0) / 100, 0);

  return (
    <div className="min-h-full bg-[#F7FAFC] px-4 md:px-6 pb-10" data-testid="funnel-page">
      <section className="-mx-4 md:-mx-6 px-4 md:px-6 pt-7 md:pt-9 pb-10 md:pb-12 bg-[linear-gradient(to_bottom,#DDF2FA_0%,#E9F7FC_48%,#F7FAFC_100%)]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#102A43]">Deal Flow Funnel</h1>
            <p className="text-[13px] md:text-sm text-[#607D8B] mt-2">Every opportunity moving through the five sales gates. Select any deal to open it.</p>
          </div>
          <div className="grid gap-2.5 md:gap-3 w-full lg:w-auto grid-cols-3">
            <HeaderMetric label="Active deals" value={active.length} />
            <HeaderMetric label="Pipeline" value={formatCr(totalValue)} />
            <HeaderMetric label="Weighted" value={formatCr(weighted)} accent />
          </div>
        </div>
      </section>

      {loading ? (
        <div className="max-w-7xl mx-auto -mt-4 text-[#607D8B] p-12 text-center">Loading funnel…</div>
      ) : error ? (
        <div className="max-w-7xl mx-auto -mt-4 p-8">
          <div className="rounded-2xl border border-[#E7B8B8] bg-[#FFF8F7] px-5 py-4 text-[13px] text-[#607D8B]" data-testid="funnel-error">
            {error.message}
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto -mt-6 relative">
          <FunnelBoard
            opps={opps}
            canMoveDeals={canMoveDeals}
            moving={moving}
            onMove={move}
            onPark={park}
            onParkRequest={setParkTarget}
            fromPath="/funnel"
            fromLabel="Funnel"
          />
        </div>
      )}

      <ParkOpportunityModal
        open={Boolean(parkTarget)}
        opportunity={parkTarget}
        onOpenChange={(open) => !open && setParkTarget(null)}
        onParked={() => { setParkTarget(null); load(); }}
      />
    </div>
  );
}

function HeaderMetric({ label, value, accent = false }) {
  return (
    <div className="min-w-0 md:min-w-[132px] rounded-xl bg-[rgba(139,169,184,0.50)] px-3.5 md:px-4 py-3 shadow-[0_7px_20px_rgba(7,83,154,0.06)]">
      <div className="text-[9px] md:text-[10px] uppercase tracking-[0.14em] text-[#07539A] truncate">{label}</div>
      <div className={`font-display text-lg md:text-xl tabular-nums mt-1 truncate ${accent ? "text-[#C76500]" : "text-[#12324A]"}`}>{value}</div>
    </div>
  );
}
